import { getElement } from "@cesium/engine";
import interact from "interactjs";
import Utils from "../basic/Utils";
import DitapPaneManager from "./DitapPaneManager";

/**
 * 다이탭 팝업 최상위 클래스.</br>
 * Ditap popup top class.
 * @alias DitapPopup
 *
 * @constructor
 * @param {Element} parent 부모 요소.</br> A parental element.
 *
 * @example
 * const parent = document.getElementById("ditapContainer");
 * const popup = new Ditap.DitapPopup(parent);
 * 
 * // # 헤더
 * const title = document.createElement("h2");
 * title.classList.add("ditap-popup-title");
 * title.innerText = "제목";
 * 
 * // # 컨텐츠
 * const subTitle = document.createElement("p");
 * subTitle.classList.add("ditap-popup-subtitle");
 * subTitle.innerText = "소제목";
 * const content = document.createElement("p");
 * content.innerText = "다이탭 팝업에 대한 내용"
 * 
 * // # 푸터
 * const footer = document.createElement("p");
 * footer.innerText = "푸터"
 * 
 * popup.prependHeader(title);
 * popup.appendContent(subTitle);
 * popup.appendContent(content);
 * popup.appendFooter(footer);
 * 
 * popup.open();

 *
 */
class DitapPopup {
  constructor(parent, ditapPaneManager) {
    this._parent = undefined;
    if (parent instanceof Element) {
      this._parent = parent;
    } else {
      throw Error("DitapPopup parent type error");
    }
    this._ditapPaneManager = undefined;
    if (ditapPaneManager instanceof DitapPaneManager) {
      this._ditapPaneManager = ditapPaneManager;
    } else {
      this._ditapPaneManager = undefined;
      // throw Error("DitapPopup manager type error");
    }
    this._wrapper = undefined;
    this._header = undefined;
    this._content = undefined;
    this._footer = undefined;
    this._closeBtn = undefined;
    this._interact = undefined;
    this._position = { x: 0, y: 0 };
    this.isMobile = Utils.isBodyWidthLessThan600();

    this.init(); // 팝업 엘리먼트 생성

    if (this._ditapPaneManager && this.isMobile) {
      const isTouchDevice = "ontouchstart" in document.documentElement;

      if (isTouchDevice) {
        this.createSwiperEvent();
      } else {
        this.createPopupBtnEvent();
      }
    }
  }

  /*
   * 팝업이 삽입될 부모 HTML 요소.</br>
   * Parent HTML element where the popup will be inserted.
   * @type {Element}
   */
  get parent() {
    return this._parent;
  }
  set parent(parent) {
    if (parent instanceof Element) {
      const clone = this.wrapper.cloneNode(true);
      parent.appendChild(clone);
      if (this.parent instanceof Element) {
        this.parent.removeChild(this.wrapper);
      }
      this._parent = parent;
      this.wrapper = clone;
    }
  }

  /*
   * 팝업을 감싸는 요소.<br>
   * Popup wrapper element.
   * @type {Element}
   */
  get wrapper() {
    return this._wrapper;
  }
  set wrapper(wrapper) {
    this._wrapper = wrapper;
  }

  /*
   * 팝업 헤더 영역 요소.<br>
   * Popup header element.
   * @type {Element}
   */
  get header() {
    return this._header;
  }
  set header(header) {
    this._header = header;
  }

  /*
   * 팝업 컨텐츠 영역 요소.</br>
   * Popup content element.
   * @type {Element}
   */
  get content() {
    return this._content;
  }
  set content(content) {
    this._content = content;
  }

  /*
   * 팝업 하단(footer) 영역 요소.</br>
   * Popup footer element.
   * @type {Element}
   */
  get footer() {
    return this._footer;
  }
  set footer(footer) {
    this._footer = footer;
  }

  /*
   * 팝업 취소 버튼 요소.</br>
   * Popup close button element.
   * @type {Element}
   */
  get closeBtn() {
    return this._closeBtn;
  }
  set closeBtn(closeBtn) {
    this._closeBtn = closeBtn;
  }

  /*
   * 인터랙티브 할당 요소.</br>
   * The interactive element to be assigned.
   * @type {object}
   * @see {@link https://interactjs.io/}
   */
  get interact() {
    return this._interact;
  }
  set interact(interact) {
    this._interact = interact;
  }

  /*
   * 팝업 위치.</br>
   * Popup position.
   * @type {Object}
   */
  get position() {
    return this._position;
  }
  set position(position) {
    this._position = position;
  }

  /*
   * DitapPopup/DitapPanel을 관리하는 클래스.<br>
   * The class that manages the DitapPopup/DitapPanel.
   * @type {DitapPaneManager}
   */
  set ditapPaneManager(ditapPaneManager) {
    this._ditapPaneManager = ditapPaneManager;
  }
  get ditapPaneManager() {
    return this._ditapPaneManager;
  }

  /*
   * 팝업 초기화.<br>
   * Popup init.
   */
  init() {
    // 엘리먼트 생성
    const wrapper = document.createElement("div");
    wrapper.classList.add(
      "ditap-popup",
      "ditap-size-popup",
      "hide",
      "js-ditap-popup"
    );

    const leftButton = document.createElement("button");
    leftButton.classList.add(
      "ditap-direction-btn",
      "ditap-custom-button",
      "ditap-popup-left",
      "disabled",
      "ditap-custom-button"
    );
    leftButton.disabled = true;

    const rightButton = document.createElement("button");
    rightButton.classList.add(
      "ditap-direction-btn",
      "ditap-custom-button",
      "ditap-popup-right",
      "disabled",
      "ditap-custom-button"
    );
    rightButton.disabled = true;

    const headerTop = document.createElement("div");
    headerTop.classList.add("ditap-popup-top");

    const btnWrap = document.createElement("div");
    btnWrap.classList.add("ditap-popup-btn-wrap");

    const closeBtn = document.createElement("button");
    closeBtn.classList.add("ditap-popup-close-btn");

    const header = document.createElement("div");
    header.classList.add("ditap-popup-header");
    const content = document.createElement("div");

    content.classList.add("ditap-popup-content");
    const footer = document.createElement("div");
    footer.classList.add("ditap-popup-footer");

    const contentWrapper = document.createElement("div");
    contentWrapper.classList.add("ditap-popup-content-wrap");

    const subTitleDiv = document.createElement("div");
    subTitleDiv.classList.add("ditap-popup-sub-wrap");

    // wrapper 안에 추가
    btnWrap.append(closeBtn);
    header.append(btnWrap);
    header.append(leftButton);
    header.append(rightButton);
    wrapper.append(header);
    wrapper.append(subTitleDiv);
    contentWrapper.append(content);
    contentWrapper.append(footer);
    wrapper.append(contentWrapper);

    // set
    this.wrapper = wrapper;
    this.headerTop = headerTop;
    this.header = header;
    this.subTitleWrapper = subTitleDiv;
    this.content = content;
    this.conetentWrapper = contentWrapper;
    this.footer = footer;
    this.closeBtn = closeBtn;
    this.btnWrap = btnWrap;
    this.leftButton = leftButton;
    this.rightButton = rightButton;

    wrapper.addEventListener("mouseover", Utils.hideLabelElement);
    wrapper.addEventListener("mouseout", Utils.showLabelElement);

    // 헤더 닫기 버튼
    this.closeBtn.addEventListener("click", () => {
      this.close();
      // this.destroy();
    });
  }

  /*
   * 닫기 버튼 위치 변경.<br>
   */
  changeCloseBtn() {
    const isMobile = Utils.isBodyWidthLessThan600();
    if (this.wrapper.classList.contains("header-top")) {
      if (isMobile) {
        this.headerTop.append(this.btnWrap);
        this.wrapper.prepend(this.headerTop);
      } else {
        this.header.append(this.btnWrap);
      }
    }
  }

  /**
   * 헤더 영역에 요소 추가.</br>
   * Append element to header.
   * @param {Element} element 추가할 요소.<br> Element to add.
   */
  appendHeader(element) {
    if (element instanceof Element) {
      this.header.append(element);
    }
  }

  /**
   * 헤더 영역 맨 앞에 요소 추가.<br>
   * Prepend element to header.
   * @param {Element} element 추가할 요소.<br>Element to add.
   */
  prependHeader(element) {
    if (element instanceof Element) {
      this.header.prepend(element);
    }
  }

  /**
   * 헤더 영역의 자식 요소 제거.</br>
   * Remove children of header.
   * @param {Element} element 제거할 요소.<br>Element to remove.
   */
  removeHeader(element) {
    if (element instanceof Element) {
      this.header.removeChild(element);
    }
  }

  /**
   * 헤더 영역의 모든 자식 요소 제거.<br>
   * Remove all children of header.
   */
  removeAllHeader() {
    this.header.innerHtml = "";
  }

  appendSubTitle(element) {
    if (element instanceof Element) {
      this.subTitleWrapper.append(element);
    }
  }

  /**
   * 컨텐츠 영역에 요소 추가.<br>
   * Append element to content.
   * @param {Element} element 추가할 요소.<br>Element to add.
   */
  appendContent(element) {
    if (element instanceof Element) {
      this.content.append(element);
    }
  }

  /**
   * 컨텐츠 영역 맨 앞에 요소 추가.</br>
   * Prepend element to content.
   * @param {Element} element 추가할 요소.<br>Element to add.
   */
  prependContent(element) {
    if (element instanceof Element) {
      this.content.prepend(element);
    }
  }

  /**
   * 컨텐츠 영역의 자식 요소 제거.</br>
   * Remove children of content.
   * @param {Element} element 제거할 요소.<br>Element to remove.
   */
  removeContent(element) {
    if (element instanceof Element) {
      this.content.removeChild(element);
    }
  }

  /**
   * 컨텐츠 영역의 모든 자식 요소 제거.</br>
   * Remove all children of content.
   */
  removeAllContent() {
    this.content.innerHtml = "";
  }

  /**
   * 푸터 영역에 요소 추가.<br>
   * Append element to footer.
   * @param {Element} element 추가할 요소.</br> Element to add.
   */
  appendFooter(element) {
    if (element instanceof Element) {
      this.footer.append(element);
    }
  }

  /**
   * 푸터 영역 맨 앞에 요소 추가.</br>
   * Prepend element to footer.
   * @param {Element} element 추가할 요소.</br>  Element to add.
   */
  prependFooter(element) {
    if (element instanceof Element) {
      this.footer.prepend(element);
    }
  }

  /**
   * 푸터 영역의 자식 요소 제거.</br>
   * Remove children of footer.
   * @param {Element} element 제거할 요소.</br> Element to remove.
   */
  removeFooter(element) {
    if (element instanceof Element) {
      this.footer.prepend(element);
    }
  }

  /**
   * 푸터 영역의 모든 자식 요소 제거.</br>
   * Remove all children of footer.
   */
  removeAllFooter() {
    this.footer.innerHtml = "";
  }

  /**
   * 팝업 열기.</br>
   * Open popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br> element If no parameter is entered, open wrapper.
   */
  open(element) {
    // 부모 자식으로 wrapper 추가
    if (this.parent instanceof Element) {
      this.parent.append(this.wrapper);
    }

    if (element) {
      element.classList.remove("hide");
    } else {
      this.wrapper.classList.remove("hide");
      this.ditapPaneManager?.addPane(element ? element : this.wrapper);
    }

    this.createResizeEvent();

    const mobileLabel = this.parent.getElementsByClassName(
      "ditap-mobile-label"
    );
    if (mobileLabel.length > 0) {
      Utils.moveUnderLabel(this.parent);
    }
  }

  /**
   * 팝업 활성화 on/off 기능.</br>
   * Function on and off.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br>If no parameter is entered, toggle wrapper.
   */
  toggle(element) {
    if (element) {
      if (element.classList.contains("hide")) {
        element.classList.remove("hide");
      } else {
        element.classList.add("hide");
      }
    } else {
      const popup = this.wrapper;
      if (popup.classList.contains("hide")) {
        popup.classList.remove("hide");
      } else {
        popup.classList.add("hide");
      }
    }
  }

  /**
   * 팝업 닫기.</br>
   * Close popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br>  If no parameter is entered, close wrapper.
   */
  close(element = undefined) {
    // 부모 자식으로 wrapper 추가
    if (this.parent instanceof Element) {
      this.wrapper.remove();
    }

    if (element) {
      element.classList.add("hide");
    } else {
      this.wrapper.classList.add("hide");
    }

    this.ditapPaneManager?.removePane(element ? element : this.wrapper);
    this.removeResizeEvent();
  }

  /**
   * 이 객체의 모든 리소스 제거.</br>
   * Remove all resources of this object.
   */
  destroy() {
    this.disableDrag();
    this.disableTouch();

    this.ditapPaneManager?.removePane(this.wrapper);
    if (this.wrapper instanceof Element) {
      this.wrapper.remove();
    }
    if (this.header instanceof Element) {
      this.header.remove();
    }
    if (this.content instanceof Element) {
      this.content.remove();
    }
    if (this.footer instanceof Element) {
      this.footer.remove();
    }
    if (this.closeBtn instanceof Element) {
      this.closeBtn.removeEventListener("click", () => {
        this.close();
      });
    }

    if (this.wrapper instanceof Element) {
      this.wrapper.removeEventListener("mouseover", Utils.hideLabelElement);
      this.wrapper.removeEventListener("mouseover", Utils.showLabelElement);
    }

    this.removeResizeEvent();
    this.removeSwiperEvent();
  }

  /*
   * 드레그하는 기능 활성화.</br>
   * Enable drag functionality.
   */
  enableDrag() {
    const wrapper = getElement(this.wrapper);
    const header = getElement(this.header);
    const closeBtn = getElement(this.closeBtn);

    this.interact = interact(wrapper).draggable({
      inertia: true,
      allowFrom: header,
      ignoreFrom: closeBtn,
      modifiers: [
        interact.modifiers.restrict({
          endOnly: true,
          restriction: this.parent ? this.parent : document.body,
          elementRect: { top: 0, left: 0, bottom: 1, right: 1 },
        }),
      ],
      listeners: {
        move: (event) => {
          if (
            !isNaN(parseFloat(parseFloat(this.wrapper.style.left))) &&
            !isNaN(parseFloat(parseFloat(this.wrapper.style.top)))
          ) {
            this.position.x = parseFloat(this.wrapper.style.left);
            this.position.y = parseFloat(this.wrapper.style.top);
          }

          this.position.x += event.dx;
          this.position.y += event.dy;

          // 팝업 위치 업데이트
          event.target.style.left = `${this.position.x}px`;
          event.target.style.top = `${this.position.y}px`;
        },
      },
    });
  }

  /*
   * 터치 기능 활성화.</br>
   * Enable touch functionality.
   */
  enableTouch() {
    const wrapper = getElement(this.wrapper);
    const header = getElement(this.header);

    // 팝업을 드래그하기 위한 변수를 선언합니다.
    let offsetX = 0;
    let offsetY = 0;
    let isDragging = false;

    // 터치 시작 이벤트 핸들러를 등록합니다.
    header.addEventListener(
      "touchstart",
      (this.touchstartEvent = (event) => {
        this.disableDrag();
        // 터치 이벤트의 클라이언트 좌표를 가져옵니다.
        const touch = event.touches[0];
        // 터치한 지점에서 팝업의 현재 위치를 빼서 오프셋을 계산합니다.
        offsetX = touch.clientX - wrapper.offsetLeft;
        offsetY = touch.clientY - wrapper.offsetTop;
        // 드래그 중임을 표시합니다.
        isDragging = true;
      })
    );

    // 터치 이동 이벤트 핸들러를 등록합니다.
    header.addEventListener(
      "touchmove",
      (this.touchmoveEvent = (event) => {
        if (!isDragging) {
          return; // 드래그 중이 아니면 종료합니다.
        }

        // 터치 이벤트의 클라이언트 좌표를 가져옵니다.
        const touch = event.touches[0];
        // 팝업의 새로운 위치를 계산합니다.
        let newLeft = touch.clientX - offsetX;
        let newTop = touch.clientY - offsetY;

        // 화면의 경계를 벗어나지 않도록 팝업의 위치를 조정합니다.
        const container = this.parent ? this.parent : document.body;
        if (newLeft < 0) {
          newLeft = 0;
        } else if (newLeft + wrapper.offsetWidth > container.offsetWidth) {
          newLeft = container.offsetWidth - wrapper.offsetWidth;
        }

        if (newTop < 0) {
          newTop = 0;
        } else if (newTop + wrapper.offsetHeight > container.offsetHeight) {
          newTop = container.offsetHeight - wrapper.offsetHeight;
        }

        this.position.x = newLeft;
        this.position.y = newTop;
        // 팝업의 위치를 설정합니다.
        wrapper.style.left = `${newLeft}px`;
        wrapper.style.top = `${newTop}px`;
      })
    );

    // 터치 종료 이벤트 핸들러를 등록합니다.
    header.addEventListener(
      "touchend",
      (this.touchendEvent = () => {
        // 드래그 상태를 해제합니다.
        isDragging = false;
      })
    );
  }

  /*
   * 드레그하는 기능 비활성화.</br>
   * Disable drag functionality.
   */
  disableDrag() {
    if (this.interact) {
      this.interact.unset();
    }
  }

  /*
   * 터치 기능 비활성화.</br>
   * Disable touch functionality.
   */
  disableTouch() {
    const header = getElement(this.header);
    if (this.touchendEvent) {
      header.removeEventListener("touchend", this.touchendEvent);
    }
    if (this.touchstartEvent) {
      header.removeEventListener("touchstartEvent", this.touchstartEvent);
    }
    if (this.touchmoveEvent) {
      header.removeEventListener("touchmove", this.touchmoveEvent);
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createResizeEvent() {
    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        const currentIsMobile = Utils.isBodyWidthLessThan600();

        if (this.isMobile !== currentIsMobile) {
          this.changeCloseBtn();
          this.isMobile = currentIsMobile;

          if (this.isMobile) {
            if (this.wrapper.classList.contains("draggable")) {
              this.wrapper.style.left = 0;
              this.wrapper.style.top = "unset";
              this.wrapper.style.transform = "";
            }
            const isTouchDevice = "ontouchstart" in document.documentElement;
            if (isTouchDevice) {
              this.createSwiperEvent();
            } else {
              this.createPopupBtnEvent();
            }
            this.disableDrag();
            this.disableTouch();
          } else {
            this.position.x = 0;
            this.position.y = 0;

            if (this.wrapper.classList.contains("draggable")) {
              this.enableDrag();
              this.enableTouch();
              Utils.moveUnderLabel(this.parent);
            }
            this.removePopupBtnEvent();
            this.removeSwiperEvent();
          }
        } else if (!currentIsMobile) {
          if (this.wrapper.classList.contains("draggable")) {
            Utils.moveUnderLabel(this.parent);
          }

          // 창 사이즈가 팝업 위치보다 작아지는 경우 팝업 위치 조정
          const container = this.parent ? this.parent : document.body;

          const rect = this.wrapper.getBoundingClientRect();
          const offsetWidth = container.offsetWidth;
          const offsetHeight = container.offsetHeight;

          this.position.x = parseFloat(this.wrapper.style.left);
          this.position.y = parseFloat(this.wrapper.style.top);

          if (this.position.x < 0) {
            this.position.x = 0;
          } else if (this.position.x + rect.width > offsetWidth) {
            this.position.x = offsetWidth - rect.width;
          }

          if (this.position.y < 0) {
            this.position.y = 0;
          } else if (this.position.y + rect.height > offsetHeight) {
            this.position.y = offsetHeight - rect.height;
          }

          // 팝업 위치 업데이트
          this.wrapper.style.left = `${this.position.x}px`;
          this.wrapper.style.top = `${this.position.y}px`;
        }
      })
    );
  }

  /*
   * 윈도우 크기 조절 시 이벤트 제거.</br>
   * Remove an event for window resize.
   */
  removeResizeEvent() {
    if (this.resizeEvent) {
      window.removeEventListener("resize", this.resizeEvent);
    }
    this.resizeEvent = undefined;
  }

  /*
   * Ditap Popup 버튼 이벤트 추가.<br>
   * Add popup button event.
   */
  createPopupBtnEvent() {
    this.leftButton.addEventListener(
      "click",
      (this.leftBtnEvent = () => {
        this.ditapPaneManager.prevPane();
      })
    );

    this.rightButton.addEventListener(
      "click",
      (this.rightBtnEvent = () => {
        this.ditapPaneManager.nextPane();
      })
    );
  }

  /*
   * Ditap Popup 버튼 이벤트 제거.<br>
   * Remove popup button event.
   */
  removePopupBtnEvent() {
    if (this.leftBtnEvent) {
      this.leftButton.removeEventListener("click", this.leftBtnEvent);
    }
    if (this.rightBtnEvent) {
      this.rightButton.removeEventListener("click", this.rightBtnEvent);
    }
  }

  /*
   * Ditap Popup 콘텐츠 영역에 스와이퍼 이벤트 추가.<br>
   * Add swiper event to the popup content area.
   */
  createSwiperEvent() {
    let startX = 0;
    let moveX = 0;

    this.header.addEventListener(
      "touchstart",
      (this.swiperTouchstartEvent = (event) => {
        // 터치 시작 지점 저장
        startX = event.touches[0].clientX;
      })
    );

    this.header.addEventListener(
      "touchmove",
      (this.swiperTouchmoveEvent = (event) => {
        // 터치 이동 지점 저장
        moveX = event.touches[0].clientX;
      })
    );

    this.header.addEventListener(
      "touchend",
      (this.swiperTouchendEvent = () => {
        // 터치 이동 거리 계산
        const diffX = moveX - startX;

        if (diffX > 0) {
          this.ditapPaneManager.prevPane();
        } else {
          this.ditapPaneManager.nextPane();
        }
      })
    );
  }

  /*
   * Ditap Popup 콘텐츠 영역에 스와이퍼 이벤트 제거.<br>
   * Remove swiper event to the popup content area.
   */
  removeSwiperEvent() {
    if (this.swiperTouchstartEvent) {
      this.header.removeEventListener("touchstart", this.swiperTouchstartEvent);
    }
    if (this.swiperTouchmoveEvent) {
      this.header.removeEventListener("touchmove", this.swiperTouchmoveEvent);
    }
    if (this.swiperTouchendEvent) {
      this.header.removeEventListener("touchend", this.swiperTouchendEvent);
    }
  }
}

export default DitapPopup;
